import React, { useState, useRef, useEffect } from 'react';
import {
    Stack,
    Input,
    Button,
    Text,
    Switch,
    Box,
    Flex,
    InputGroup,
    InputLeftElement
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { DateRangePicker } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

const Filters = ({ filters, setFilters, editMode, setEditMode }) => {
    const input = useRef(null);
    const mobileInput = useRef(null);
    const [inputValue, setInputValue] = useState('');
    const [timeValue, setTimeValue] = useState(1);
    const [isSelectOpen, setIsSelectOpen] = useState(false);

    const [dateRange, setDateRange] = useState({
        startDate: new Date(),
        endDate: new Date(),
        key: 'selection',
    });

    // Debounce input handler
    const debounceTimeout = useRef();
    const inputHandler = () => {
        const value = input?.current?.value ? input?.current?.value : mobileInput?.current?.value;
        setInputValue(value ? value : '');
    };
    useEffect(() => {
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }
        debounceTimeout.current = setTimeout(() => {
            const val = inputValue?.trim ? inputValue.trim() : '';
            if (val !== '') {
                setFilters(prev => ({
                    ...prev,
                    callerPhoneNumber: val
                }));
            }
        }, 400);
        return () => {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
        };
    }, [inputValue, setFilters]);

    const handleSelect = (ranges) => {
        setDateRange({
            startDate: ranges.selection.startDate,
            endDate: ranges.selection.endDate,
            key: 'selection',
        });
    };
    function toLocalIsoString(date) {
        const pad = n => String(n).padStart(2, '0');
        const year = date.getFullYear();
        const month = pad(date.getMonth() + 1);
        const day = pad(date.getDate());
        const hour = pad(date.getHours());
        const min = pad(date.getMinutes());
        const sec = pad(date.getSeconds());
        const ms = String(date.getMilliseconds()).padStart(3, '0');
        const tzOffset = -date.getTimezoneOffset();
        const sign = tzOffset >= 0 ? '+' : '-';
        const absOffset = Math.abs(tzOffset);
        const tzHour = pad(Math.floor(absOffset / 60));
        const tzMin = pad(absOffset % 60);
        return `${year}-${month}-${day}T${hour}:${min}:${sec}.${ms}${sign}${tzHour}:${tzMin}`;
    }

    const onChangeSelect = (first, second) => {
        setIsSelectOpen(false);

        // Update parent filters with selected date range

        setFilters(prev => ({
            ...prev,
            startDatetime: toLocalIsoString(first),
            endDatetime: toLocalIsoString(second)
        }));
    };

    // Time filter buttons
    const dateFilters = [
        {
            name: 'Today',
            value: 1,
            changeFilters: () => {
                setTimeValue(1);
                setIsSelectOpen(false);
                const today = new Date();
                const startOfDay = new Date(today.setHours(0, 0, 0, 0));
                const endOfDay = new Date(new Date().setHours(23, 59, 59, 999));
                setFilters(prev => ({
                    ...prev,
                    startDatetime: toLocalIsoString(startOfDay),
                    endDatetime: toLocalIsoString(endOfDay)
                }));
            }
        },
        {
            name: 'Yesterday',
            value: 2,
            changeFilters: () => {
                console.log('Filter: Yesterday');
                setTimeValue(2);
                setIsSelectOpen(false);
                const today = new Date();
                const yesterday = new Date(today);
                yesterday.setDate(today.getDate() - 1);
                const startOfYesterday = new Date(yesterday.setHours(0, 0, 0, 0));
                const endOfYesterday = new Date(yesterday.setHours(23, 59, 59, 999));
                setFilters(prev => ({
                    ...prev,
                    startDatetime: toLocalIsoString(startOfYesterday),
                    endDatetime: toLocalIsoString(endOfYesterday)
                }));
            }
        },
        {
            name: 'Last 7 days',
            value: 3,
            changeFilters: () => {
                setTimeValue(3);
                setIsSelectOpen(false);
                const today = new Date();
                const sevenDaysAgo = new Date(today);
                sevenDaysAgo.setDate(today.getDate() - 7);
                setFilters(prev => ({
                    ...prev,
                    startDatetime: toLocalIsoString(sevenDaysAgo),
                    endDatetime: toLocalIsoString(today)
                }));
            }
        },
        {
            name: 'This Month',
            value: 4,
            changeFilters: () => {
                setTimeValue(4);
                setIsSelectOpen(false);
                const now = new Date();
                const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
                const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
                setFilters(prev => ({
                    ...prev,
                    startDatetime: toLocalIsoString(startOfMonth),
                    endDatetime: toLocalIsoString(endOfMonth)
                }));
            }
        },
        {
            name: 'Last Month',
            value: 5,
            changeFilters: () => {
                console.log('Filter: Last Month');
                setTimeValue(5);
                setIsSelectOpen(false);
                const now = new Date();
                const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
                const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
                setFilters(prev => ({
                    ...prev,
                    startDatetime: toLocalIsoString(startOfLastMonth),
                    endDatetime: toLocalIsoString(endOfLastMonth)
                }));
            }
        },
        {
            name: 'Custom',
            value: 6,
            changeFilters: () => {
                console.log('Filter: Custom');
                setTimeValue(6);
                setIsSelectOpen(true);
            }
        }
    ];

    return (
        <>
            <Stack direction='row' justifyContent='space-between' flexWrap='wrap'>
                {/* Use InputGroup and InputLeftElement for search icon */}
                <InputGroup width='288px'>
                    <Input
                        fontSize='calc(12px + 4 * (100vw / 1920))'
                        ref={input}
                        onChange={inputHandler}
                        placeholder='Search by tel number'
                        pl='10'
                    />
                    <InputLeftElement pointerEvents="none">
                        <FontAwesomeIcon icon={faSearch} color='gray' />
                    </InputLeftElement>
                </InputGroup>
                <Stack direction='row' alignItems='center' gap={4}>
                    <Stack alignItems='center' direction='row'>
                        <Text>Trash</Text>
                        <Switch
                            onChange={(e) => {

                                setFilters((prev) => ({
                                    ...prev,
                                    isDelete: e.target.checked,
                                }));
                            }}
                            isChecked={filters.isDelete}
                        />
                    </Stack>
                    <Stack alignItems='center' direction='row'>
                        <Text>Blocked</Text>
                        <Switch
                            onChange={(e) => {
                                setFilters((prev) => ({
                                    ...prev,
                                    blockedOnly: e.target.checked,
                                }));
                            }}
                            isChecked={filters.blockedOnly}
                        />
                    </Stack>
                    {!editMode && (<Button
                        size='sm'
                        variant={'outline'}
                        colorScheme='blue'
                        onClick={() => setEditMode((prev) => !prev)}
                    >
                        Edit
                    </Button>)}
                </Stack>
            </Stack>

            <Stack direction='row' spacing='3' flexWrap='wrap'>
                {dateFilters.map((time) => {
                    if (time.name === 'Custom') {
                        return (
                            <Box key={time.value} position='relative' display='inline-block'>
                                <Button
                                    variant={time?.value === timeValue ? 'solid' : 'outline'}
                                    _hover={{ color: '#3182CE' }}
                                    onClick={time.changeFilters}
                                    size='sm'
                                >
                                    {time.name}
                                </Button>
                                {isSelectOpen && (
                                    <Box
                                        p='10px'
                                        borderRadius='6px'
                                        border='3px solid #3182CE'
                                        pos='absolute'
                                        zIndex='1'
                                        left='0'
                                        top='100%'
                                        mt='8px'
                                        bg='white'
                                        minW='260px'
                                    >
                                        <DateRangePicker showDateDisplay={false} ranges={[dateRange]} onChange={handleSelect} />
                                        <Flex justifyContent='space-between'>
                                            <Button variant='outline' onClick={() => setIsSelectOpen(false)}>
                                                Cancel
                                            </Button>
                                            <Button colorScheme='blue' onClick={() => onChangeSelect(dateRange.startDate, dateRange.endDate)}>
                                                Save
                                            </Button>
                                        </Flex>
                                    </Box>
                                )}
                            </Box>
                        );
                    }
                    return (
                        <Button
                            key={time.value}
                            variant={time?.value === timeValue ? 'solid' : 'outline'}
                            _hover={{ color: '#3182CE' }}
                            onClick={time.changeFilters}
                            size='sm'
                        >
                            {time.name}
                        </Button>
                    );
                })}
            </Stack>
        </>
    );
};

export default Filters;